import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';
import { adminGetTrackAudit } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface AuditEntry {
  id: number;
  trackId: number;
  action: string;
  actor: string;
  resultData: any;
  integrityHash: string;
  createdAt: number;
}

interface AuditIntegrity {
  valid: boolean;
  brokenAtId?: number;
  totalRecords: number;
}

interface TrackAuditModalProps {
  trackId: number;
  trackTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  UPLOAD: 'Track Uploaded',
  INITIATE_AI_SCAN: 'AI Scan Initiated',
  INITIATE_CONTENT_ID_SCAN: 'Content ID Scan Initiated',
  AI_SCAN_RESULT: 'AI Scan Result',
  CONTENT_ID_RESULT: 'Content ID Result',
  AI_SCAN_STATUS_CHANGE: 'AI Verification Status Change',
  CONTENT_ID_STATUS_CHANGE: 'Content ID Status Change',
  PROOF_OF_HUMANITY_REQUESTED: 'Proof of Humanity Requested',
  TRACK_DELETED: 'Track Deleted',
  PUBLISHED_TO_VAULT: 'Published to Vault',
};

const ACTION_COLORS: Record<string, string> = {
  UPLOAD: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  INITIATE_AI_SCAN: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  INITIATE_CONTENT_ID_SCAN: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  AI_SCAN_RESULT: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  CONTENT_ID_RESULT: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  AI_SCAN_STATUS_CHANGE: 'text-green-400 bg-green-400/10 border-green-400/20',
  CONTENT_ID_STATUS_CHANGE: 'text-green-400 bg-green-400/10 border-green-400/20',
  PROOF_OF_HUMANITY_REQUESTED: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  TRACK_DELETED: 'text-red-400 bg-red-400/10 border-red-400/20',
  PUBLISHED_TO_VAULT: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
};

export default function TrackAuditModal({ trackId, trackTitle, isOpen, onClose }: TrackAuditModalProps) {
  const { token } = useAuthStore();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [integrity, setIntegrity] = useState<AuditIntegrity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !token) return;
    setLoading(true);
    setError(null);

    adminGetTrackAudit(trackId, token)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to fetch audit log');
        const data = await res.json() as any;
        setEntries(data.data || []);
        setIntegrity(data.integrity || null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isOpen, trackId, token]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="relative bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <div>
              <h2 className="text-lg font-semibold text-white">Track Audit Trail</h2>
              <p className="text-sm text-text-secondary mt-0.5 truncate max-w-md">{trackTitle}</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-5 h-5 text-text-secondary" />
            </button>
          </div>

          {/* Integrity Status */}
          {integrity && (
            <div className={`mx-5 mt-4 px-4 py-3 rounded-lg border flex items-center gap-3 ${
              integrity.valid
                ? 'bg-green-500/5 border-green-500/20'
                : 'bg-red-500/5 border-red-500/20'
            }`}>
              {integrity.valid ? (
                <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
              ) : (
                <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium ${integrity.valid ? 'text-green-400' : 'text-red-400'}`}>
                  Audit Integrity: {integrity.valid ? 'VERIFIED' : 'COMPROMISED'}
                </span>
                <span className="text-xs text-text-muted ml-2">
                  ({integrity.totalRecords} record{integrity.totalRecords !== 1 ? 's' : ''})
                </span>
                {!integrity.valid && integrity.brokenAtId && (
                  <p className="text-xs text-red-400/80 mt-0.5">
                    Chain broken at record #{integrity.brokenAtId}. Data may have been tampered with.
                  </p>
                )}
              </div>
              <Shield className={`w-4 h-4 flex-shrink-0 ${integrity.valid ? 'text-green-400/40' : 'text-red-400/40'}`} />
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-electric-cyan animate-spin" />
                <span className="ml-2 text-text-secondary text-sm">Loading audit trail...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-400 text-sm">{error}</div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-text-muted text-sm">
                No audit records for this track yet.
              </div>
            ) : (
              <div className="space-y-3">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-[#121212] border border-white/5 rounded-lg p-4 hover:border-white/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-block px-2 py-0.5 text-xs font-mono rounded border ${ACTION_COLORS[entry.action] || 'text-text-muted bg-white/5 border-white/10'}`}>
                            {ACTION_LABELS[entry.action] || entry.action}
                          </span>
                          <span className="text-xs text-text-muted">
                            by <span className="text-text-secondary">{entry.actor}</span>
                          </span>
                        </div>
                        {entry.resultData && (
                          <pre className="mt-2 text-xs text-text-muted bg-black/30 rounded p-2 overflow-x-auto max-h-24 font-mono whitespace-pre-wrap break-all">
                            {JSON.stringify(entry.resultData, null, 2)}
                          </pre>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs text-text-muted whitespace-nowrap">
                          {new Date(entry.createdAt).toLocaleString()}
                        </span>
                        <p className="text-[10px] text-text-muted/50 font-mono mt-0.5 truncate max-w-[120px]" title={entry.integrityHash}>
                          #{entry.integrityHash.slice(0, 8)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 px-5 py-3 flex items-center justify-between">
            <span className="text-xs text-text-muted">
              Records are immutable and chain-hashed for tamper evidence.
            </span>
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-sm bg-surface border border-white/10 rounded-lg hover:border-white/20 transition-colors text-text-secondary"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
